import { cn } from '@heidi/utils';

import { focusRing, focusRingInvalid } from '../styles/focus-ring';

/** Everything except the invalid tier — shared with the experimental recipe. */
export const checkboxRootBaseClasses = cn(
  'group peer relative size-4 shrink-0 rounded-sm border border-border-selected',
  'transition-colors duration-150 ease-out',
  focusRing,
  'disabled:cursor-not-allowed disabled:opacity-50',
  'data-[state=checked]:border-background-primary data-[state=checked]:bg-background-primary data-[state=checked]:text-text-primary-invert',
  'data-[state=indeterminate]:border-background-primary data-[state=indeterminate]:bg-background-primary data-[state=indeterminate]:text-text-primary-invert',
  'enabled:data-[state=unchecked]:hover:border-border-selected/70',
  'enabled:data-[state=checked]:hover:border-background-primary-pressed/70',
  'enabled:data-[state=indeterminate]:hover:border-background-primary-pressed/70',
  'motion-reduce:transition-none'
);

export const checkboxRootClasses = cn(checkboxRootBaseClasses, focusRingInvalid);

// Grid centering overlaps both glyphs in one cell so they can cross-fade.
// Both stay mounted via `forceMount` on the Indicator.
export const checkboxIndicatorClasses = cn('grid place-items-center text-current');

/**
 * The glyph draws in via `stroke-dashoffset` (dasharray = path length, 24
 * for Check, 14 for Minus in Lucide's 24×24 viewBox). Entry: 100ms
 * ease-out-quart with a 75ms delay so the box-fill lands first. Exit:
 * 100ms opacity-only — the variant's wider transition-property list drops
 * away when the selector unmatches, so the stroke snaps back instead of
 * interpolating. Pure CSS transitions, so mount with `data-state=checked`
 * lands statically.
 */
export const checkboxIconClasses = {
  check: cn(
    'col-start-1 row-start-1 size-3.5',
    'opacity-0 [stroke-dasharray:24] [stroke-dashoffset:-24]',
    'transition-opacity duration-100 ease-out',
    'group-data-[state=checked]:transition-[opacity,stroke-dashoffset]',
    'group-data-[state=checked]:duration-100',
    'group-data-[state=checked]:delay-75',
    'group-data-[state=checked]:ease-out-quart',
    'group-data-[state=checked]:opacity-100',
    'group-data-[state=checked]:[stroke-dashoffset:0]',
    'motion-reduce:transition-none'
  ),
  indeterminate: cn(
    'col-start-1 row-start-1 size-3.5',
    'opacity-0 [stroke-dasharray:14] [stroke-dashoffset:14]',
    'transition-opacity duration-100 ease-out',
    'group-data-[state=indeterminate]:transition-[opacity,stroke-dashoffset]',
    'group-data-[state=indeterminate]:duration-100',
    'group-data-[state=indeterminate]:delay-75',
    'group-data-[state=indeterminate]:ease-out-quart',
    'group-data-[state=indeterminate]:opacity-100',
    'group-data-[state=indeterminate]:[stroke-dashoffset:0]',
    'motion-reduce:transition-none'
  ),
} as const;

// Opt-in extended hit zone. Adds an invisible `::after` overlay that captures
// pointer events ~12px horizontally and ~8px vertically beyond the visible box.
// Off by default because the expanded zone can intercept clicks/hovers on
// nearby siblings. `relative` is included so the overlay anchors to the
// checkbox root.
export const checkboxExpandedHitAreaClasses = cn(
  "relative after:absolute after:-inset-x-3 after:-inset-y-2 after:content-['']"
);
